// ─────────────────────────────────────────
// CoursePage.js — course leaderboard
// ─────────────────────────────────────────
// Shows every round ever logged at a specific course,
// ranked by score (best score = #1).
// Reached by clicking any course name in the Feed or Passport.
//
// The course name comes from the URL: /course?name=Pebble+Beach+GL

import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import AvatarImg from '../components/AvatarImg'

export default function CoursePage({ session }) {
  // useSearchParams reads the ?name= part of the URL
  const [searchParams] = useSearchParams()
  const courseName = searchParams.get('name') || 'Unknown Course'

  const [rounds, setRounds] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchCourseRounds()
  }, [courseName])

  async function fetchCourseRounds() {
    // Fetch all rounds at this course, joined with the poster's profile
    // ilike = case-insensitive match, so "pebble beach" and "Pebble Beach" both work
    const { data } = await supabase
      .from('rounds')
      .select(`*, profiles ( username, handicap, avatar_emoji, avatar_url )`)
      .ilike('course_name', courseName)
      .order('score', { ascending: true }) // best score first

    setRounds(data || [])
    setLoading(false)
  }

  // ── HELPERS ─────────────────────────────────────────────────

  function scoreDiffLabel(score, par) {
    if (!par) return null
    const diff = score - par
    if (diff === 0) return 'E'
    if (diff > 0) return `+${diff}`
    return `${diff}`
  }

  function scoreBadgeColor(score, par) {
    if (!par) return '#1a7a3c'
    const diff = score - par
    if (diff < 0) return '#1a7a3c'
    if (diff === 0) return '#2980b9'
    return '#c0392b'
  }

  function formatDate(dateStr) {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }

  // The most common par logged for this course (used for scoring display)
  const coursePar = rounds.length > 0
    ? rounds.find(r => r.par)?.par
    : null

  const bestScore = rounds.length > 0 ? rounds[0].score : null

  // ── RENDER ──────────────────────────────────────────────────

  return (
    <div style={styles.page}>

      {/* ── TOP BAR ── */}
      <div style={styles.topbar}>
        <Link to="/" style={styles.back}>← Feed</Link>
        <div style={styles.topbarTitle}>Course Board</div>
        <div style={{ width: 60 }} />
      </div>

      <div style={styles.body}>

        {/* ── COURSE HEADER ── */}
        <div style={styles.courseHeader}>
          <div style={styles.courseEmoji}>⛳</div>
          <div style={styles.courseName}>{courseName}</div>
          {coursePar && (
            <div style={styles.coursePar}>Par {coursePar}</div>
          )}

          {/* Summary chips */}
          {!loading && rounds.length > 0 && (
            <div style={styles.summaryRow}>
              <div style={styles.summaryChip}>
                <div style={styles.summaryNum}>{rounds.length}</div>
                <div style={styles.summaryLabel}>Rounds logged</div>
              </div>
              <div style={styles.summaryChip}>
                <div style={styles.summaryNum}>{bestScore}</div>
                <div style={styles.summaryLabel}>Course record</div>
              </div>
              <div style={styles.summaryChip}>
                <div style={styles.summaryNum}>
                  {/* Count unique players */}
                  {new Set(rounds.map(r => r.user_id)).size}
                </div>
                <div style={styles.summaryLabel}>Players</div>
              </div>
            </div>
          )}
        </div>

        {/* ── LEADERBOARD ── */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Leaderboard</div>

          {loading && (
            <div style={styles.empty}>Loading... ⛳</div>
          )}

          {!loading && rounds.length === 0 && (
            <div style={styles.empty}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>🏌️</div>
              <div style={{ fontWeight: 700, marginBottom: 6 }}>No rounds here yet</div>
              <div style={{ fontSize: 13, color: '#6b7c6e', marginBottom: 20 }}>
                Be the first to log a round at {courseName}
              </div>
              <Link to="/log" style={styles.logBtn}>Log a Round →</Link>
            </div>
          )}

          {!loading && rounds.length > 0 && rounds.map((round, index) => {
            const diff = scoreDiffLabel(round.score, round.par)
            const isTopThree = index < 3
            const medals = ['🥇', '🥈', '🥉']

            return (
              <div
                key={round.id}
                style={{
                  ...styles.leaderboardRow,
                  // Highlight top 3 with a subtle background
                  background: isTopThree ? '#f0faf3' : 'transparent',
                  borderBottom: index < rounds.length - 1 ? '1px solid #f0f4f1' : 'none'
                }}
              >
                {/* Rank */}
                <div style={styles.rank}>
                  {isTopThree
                    ? <span style={{ fontSize: 20 }}>{medals[index]}</span>
                    : <span style={styles.rankNum}>{index + 1}</span>
                  }
                </div>

                {/* Avatar + name */}
                <div style={styles.rowAvatar}>
                  <AvatarImg profile={round.profiles} />
                </div>
                <div style={styles.rowInfo}>
                  <div style={styles.rowName}>@{round.profiles?.username || 'golfer'}</div>
                  <div style={styles.rowDate}>{formatDate(round.created_at)}</div>
                </div>

                {/* Score */}
                <div style={{
                  ...styles.rowScore,
                  background: scoreBadgeColor(round.score, round.par)
                }}>
                  <div style={styles.rowScoreNum}>{round.score}</div>
                  {diff && <div style={styles.rowScoreDiff}>{diff}</div>}
                </div>
              </div>
            )
          })}
        </div>

        {/* ── LOG THIS COURSE ── */}
        <div style={styles.ctaRow}>
          <Link
            to={`/log?course=${encodeURIComponent(courseName)}`}
            style={styles.logBtn}
          >
            ＋ Log a Round Here
          </Link>
        </div>

      </div>
    </div>
  )
}

// ── STYLES ───────────────────────────────────────────────────

const styles = {
  page: { minHeight: '100vh', background: '#f4f7f5', fontFamily: "'Barlow', sans-serif" },

  topbar: {
    background: '#0e1f13', padding: '14px 20px',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    position: 'sticky', top: 0, zIndex: 10
  },
  back: { color: '#c8f135', textDecoration: 'none', fontSize: 14, fontWeight: 600, minWidth: 60 },
  topbarTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 18, color: 'white', letterSpacing: 1
  },

  body: { maxWidth: 600, margin: '0 auto', padding: '16px 16px 40px' },

  // ── Course header ─────────────────────────────────────
  courseHeader: {
    background: '#0e1f13', borderRadius: 20, padding: '28px 20px 20px',
    marginBottom: 14, textAlign: 'center'
  },
  courseEmoji: { fontSize: 40, marginBottom: 10 },
  courseName: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 28, color: 'white',
    letterSpacing: 1, lineHeight: 1.1, marginBottom: 6
  },
  coursePar: { fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 16 },
  summaryRow: { display: 'flex', gap: 10, justifyContent: 'center' },
  summaryChip: {
    background: 'rgba(255,255,255,0.07)', borderRadius: 12,
    padding: '10px 16px', textAlign: 'center', flex: 1
  },
  summaryNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 22, color: '#c8f135', lineHeight: 1
  },
  summaryLabel: { fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.8, marginTop: 4 },

  // ── Section ───────────────────────────────────────────
  section: {
    background: 'white', borderRadius: 18,
    overflow: 'hidden', marginBottom: 14,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  sectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 14, color: '#0e1f13',
    letterSpacing: 1, textTransform: 'uppercase',
    padding: '16px 16px 12px',
    borderBottom: '1px solid #f0f4f1'
  },

  empty: { textAlign: 'center', padding: '32px 16px', color: '#6b7c6e' },

  // ── Leaderboard row ───────────────────────────────────
  leaderboardRow: {
    display: 'flex', alignItems: 'center',
    padding: '12px 16px', gap: 10
  },
  rank: { width: 28, textAlign: 'center', flexShrink: 0 },
  rankNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 800, fontSize: 16, color: '#6b7c6e'
  },
  rowAvatar: {
    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
  },
  rowInfo: { flex: 1 },
  rowName: { fontWeight: 700, fontSize: 13, color: '#0e1f13' },
  rowDate: { fontSize: 10, color: '#6b7c6e', marginTop: 2 },
  rowScore: {
    borderRadius: 10, padding: '4px 12px', textAlign: 'center', minWidth: 52
  },
  rowScoreNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 22, color: 'white', lineHeight: 1.1
  },
  rowScoreDiff: { fontSize: 10, color: 'rgba(255,255,255,0.8)', fontWeight: 600 },

  // ── CTA ───────────────────────────────────────────────
  ctaRow: { textAlign: 'center' },
  logBtn: {
    display: 'inline-block', background: '#c8f135', color: '#0e1f13',
    border: 'none', borderRadius: 12, padding: '14px 28px',
    fontSize: 15, fontWeight: 800, cursor: 'pointer',
    textDecoration: 'none', fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 1
  }
}
